import type { FunctionComponent, ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { useMcpContext } from '../McpContextProvider';
import { bitbucketWorkspaceSelectorMessages } from './messages';
import { orgMismatchMessages } from '../orgMismatchMessages';
import MultiSelect from '../MultiSelect/MultiSelect';
import FieldLabel from '../MultiSelect/FieldLabel';
import {
  getAvatarOptionChipColor,
  getAvatarOptionIcon,
} from '../MultiSelect/avatarOption';
import {
  getOrderedPolicySources,
  grantedNoPermissions,
} from '../domain/policyState';
import { getOrgOptionState } from '../domain/orgMatch';
import type { BitbucketWorkspace } from '#definitions/consent-info';

const BITBUCKET_SELECT_INPUT_ID =
  'oauth-consent-mcp-bitbucket-workspace-select';

// Pure functions of the item, so they live at module scope rather than being
// rebuilt in the component body.
const toValue = (ws: BitbucketWorkspace): string => ws.ari;
const getLabel = (ws: BitbucketWorkspace): string => ws.name;

/**
 * Bitbucket workspace picker for the MCP consent screen.
 *
 * Rendered below the Atlassian site selector whenever /consent/info returned at
 * least one workspace. An empty list hides the field entirely rather than
 * showing one with nothing to pick. There is no
 * `oauth-consent_bitbucket-mcp-workspace` check in this flow — see
 * useBitbucketWorkspaces. The selected workspace's ARI is submitted as
 * an additional `selectedWorkspace` param on Accept (see McpFlowParameters).
 *
 * Hidden as well whenever the Bitbucket check does not speak for the flow, i.e.
 * 'bitbucket' is absent from `getOrderedPolicySources` — which today means a
 * Trello-only token. That list is what the screen aggregates permissions over
 * and what `hasFailedPolicyCheck` / `getPrimaryPolicyErrorSource` are scoped
 * to, so a field rendered outside it would be a target the user can select and
 * submit while every permission it grants is filtered out of the screen, and a
 * failed check for it could reach neither the full-panel alert nor this field's
 * own message slot. The visibility rule is read off that same list rather than
 * off the scopes so the two cannot drift: participation otherwise depends only
 * on /consent/info's workspace list, which knows nothing about scopes.
 *
 * A successful check that grants nothing is reported in this field's own message
 * slot, reading only `policies.bitbucket` so a site result never surfaces here.
 * A *failed* request is shown full-panel by McpPolicyAlerts instead, and the
 * field stays mounted throughout so the selection can be changed and retried.
 */
const BitbucketWorkspaceSelector: FunctionComponent =
  (): ReactElement | null => {
    const {
      isBitbucketWorkspaceAccessEnabled,
      availableBitbucketWorkspaces,
      selectedBitbucketWorkspace,
      setSelectedBitbucketWorkspace,
      policies,
      hasOrgMismatch,
      siteOrgId,
      scopeClassification,
    } = useMcpContext();
    const { formatMessage } = useIntl();

    // A selected site's org disables other-org workspaces, so they stay listed
    // but cannot be picked. The messages below take priority over the helper,
    // so they gate it.
    const { isItemDisabled, helpKind } = getOrgOptionState({
      items: availableBitbucketWorkspaces,
      otherOrgId: siteOrgId,
      getOrgId: (ws: BitbucketWorkspace) => ws.orgId ?? null,
      canShowHelp:
        !selectedBitbucketWorkspace &&
        !hasOrgMismatch &&
        !grantedNoPermissions(policies.bitbucket),
    });

    const message = hasOrgMismatch
      ? formatMessage(orgMismatchMessages.orgMismatch)
      : grantedNoPermissions(policies.bitbucket)
        ? formatMessage(bitbucketWorkspaceSelectorMessages.noPermissions)
        : helpKind === 'empty'
          ? formatMessage(bitbucketWorkspaceSelectorMessages.noOrgWorkspaces)
          : helpKind === 'constraint'
            ? formatMessage(bitbucketWorkspaceSelectorMessages.orgConstraint)
            : undefined;
    // Hidden unless there is at least one workspace to grant, and unless the
    // Bitbucket check speaks for this flow at all — see the docblock above.
    if (
      !isBitbucketWorkspaceAccessEnabled ||
      !getOrderedPolicySources(scopeClassification).includes('bitbucket')
    ) {
      return null;
    }

    return (
      <MultiSelect<BitbucketWorkspace, string>
        inputId={BITBUCKET_SELECT_INPUT_ID}
        items={availableBitbucketWorkspaces}
        value={
          selectedBitbucketWorkspace ? [selectedBitbucketWorkspace.ari] : []
        }
        toValue={toValue}
        getLabel={getLabel}
        getIcon={getAvatarOptionIcon}
        getChipColor={getAvatarOptionChipColor}
        isItemDisabled={isItemDisabled}
        message={message}
        messageTone={helpKind === 'none' ? 'error' : 'information'}
        placeholder={formatMessage(
          bitbucketWorkspaceSelectorMessages.placeholder,
        )}
        renderLabel={() => (
          <FieldLabel>
            {formatMessage(bitbucketWorkspaceSelectorMessages.label)}
          </FieldLabel>
        )}
        onChange={(_aris, items) =>
          setSelectedBitbucketWorkspace(items[0] ?? null)
        }
      />
    );
  };

export default BitbucketWorkspaceSelector;
